"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CreateDealInviteSchema = exports.DealInviteSchema = exports.DEAL_INVITE_STAGES = exports.DEAL_INVITE_STATUSES = void 0;
exports.inviteStage = inviteStage;
exports.inviteIsLive = inviteIsLive;
exports.inviteFirstName = inviteFirstName;
exports.inviteOfferUrl = inviteOfferUrl;
const zod_1 = require("zod");
/**
 * DEAL INVITES — a published deal, sent personally to one prospect.
 *
 * A super admin names a prospect (contact, business, email) and picks a deal.
 * The prospect gets a link to a public offer page (platform-app
 * `/#/offer/<token>`) that greets them by name and lays out the deal. Every
 * "Enroll" on that page goes to the EXISTING signup with `?deal=<code>` — the
 * invite creates nothing itself. It only records how far the prospect got.
 *
 * ---------------------------------------------------------------------------
 * THE TOKEN IS THE ONLY PUBLIC KEY
 * ---------------------------------------------------------------------------
 *
 * The public read is by `token` (unguessable, 22+ url-safe chars), never by
 * `inviteId`. The public payload carries the prospect's first name, business
 * and the operator's note — nothing else about the invite (no email, no phone,
 * no lead id, no sender email).
 *
 * ---------------------------------------------------------------------------
 * STAGE IS DERIVED, NEVER TRUSTED AS STORED
 * ---------------------------------------------------------------------------
 *
 * `status` is what the server wrote (`sent`, `revoked`, `signed_up`); opening
 * the page bumps `openCount` without rewriting it, and nothing sweeps expired
 * rows. `inviteStage` folds all of that into the one value every surface shows,
 * so the tracker and the offer page cannot disagree about a link.
 */
exports.DEAL_INVITE_STATUSES = ['sent', 'revoked', 'signed_up'];
exports.DEAL_INVITE_STAGES = ['sent', 'opened', 'signed_up', 'expired', 'revoked'];
exports.DealInviteSchema = zod_1.z.object({
    inviteId: zod_1.z.string().min(1),
    /** Public, unguessable. What the offer link carries. */
    token: zod_1.z.string().min(16),
    contactName: zod_1.z.string().min(1).max(120),
    businessName: zod_1.z.string().min(1).max(160),
    /** Lower-cased. Signup matches on this plus the deal. */
    email: zod_1.z.string().email(),
    phone: zod_1.z.string().max(40).optional(),
    /** Shown on the offer page and in the email. Plain text. */
    note: zod_1.z.string().max(1000).optional(),
    dealId: zod_1.z.string().min(1),
    /** Snapshotted at send so the list reads without a deal lookup. */
    dealCode: zod_1.z.string().min(1),
    dealName: zod_1.z.string().min(1),
    /** Epoch ms. Absent = lasts while the deal is active. */
    expiresAt: zod_1.z.number().optional(),
    status: zod_1.z.enum(exports.DEAL_INVITE_STATUSES).default('sent'),
    openCount: zod_1.z.number().int().min(0).default(0),
    firstOpenedAt: zod_1.z.number().optional(),
    lastOpenedAt: zod_1.z.number().optional(),
    signedUpAt: zod_1.z.number().optional(),
    tenantId: zod_1.z.string().optional(),
    leadId: zod_1.z.string().optional(),
    /** Epoch ms the linked lead was last sent `offer_opened` (Leads Desk — at most once an hour). */
    leadOpenNotifiedAt: zod_1.z.number().optional(),
    sentByEmail: zod_1.z.string().optional(),
    sentByName: zod_1.z.string().optional(),
    /** How many times the email went out (first send + resends). 0 = link copied only. */
    sendCount: zod_1.z.number().int().min(0).default(0),
    lastSentAt: zod_1.z.number().optional(),
    revokedAt: zod_1.z.number().optional(),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number(),
});
/** What the Send Deal dialog posts. */
exports.CreateDealInviteSchema = zod_1.z.object({
    contactName: zod_1.z.string().trim().min(1).max(120),
    businessName: zod_1.z.string().trim().min(1).max(160),
    email: zod_1.z.string().trim().toLowerCase().email(),
    phone: zod_1.z.string().trim().max(40).optional(),
    note: zod_1.z.string().trim().max(1000).optional(),
    dealId: zod_1.z.string().min(1),
    /** 1–90 days. Absent = until the deal itself stops. */
    expiresInDays: zod_1.z.number().int().min(1).max(90).optional(),
    leadId: zod_1.z.string().optional(),
    /** false = create the invite and hand back its link without emailing. */
    sendEmail: zod_1.z.boolean().default(true),
});
/** The one stage every surface shows. `now` is injectable for tests. */
function inviteStage(invite, now = Date.now()) {
    if (invite.status === 'signed_up')
        return 'signed_up';
    if (invite.status === 'revoked')
        return 'revoked';
    if (typeof invite.expiresAt === 'number' && invite.expiresAt <= now)
        return 'expired';
    return (invite.openCount ?? 0) > 0 ? 'opened' : 'sent';
}
/** Can the offer page still be shown for this invite? */
function inviteIsLive(invite, now = Date.now()) {
    const stage = inviteStage(invite, now);
    return stage === 'sent' || stage === 'opened';
}
/** The first word of the contact name — "Hi Maria". */
function inviteFirstName(contactName) {
    return (contactName || '').trim().split(/\s+/)[0] || '';
}
/** `https://rubytill.com/#/offer/<token>` — the platform-app public route. */
function inviteOfferUrl(frontendUrl, token) {
    return `${String(frontendUrl || '').replace(/\/+$/, '')}/#/offer/${encodeURIComponent(token)}`;
}
//# sourceMappingURL=dealInvite.js.map